import { useState, useEffect, useCallback } from 'react';
import { Modifier, Idol, Stat } from 'types';
import { ContentType, Mod } from 'types/mods';
import { ITEM_TYPES } from 'constants/index';
import { 
  getIdolSizeFromItemType, 
  getAvailablePrefixes,
  getAvailableSuffixes
} from 'utils/mod-utils';
import { parseIdolFromPaste } from '../../IdolPasteHandler';

export const useIdolForm = (onAddIdol: (idol: Idol) => void, onClose: () => void) => {
  // Form state
  const [newIdolName, setNewIdolName] = useState<string>('');
  const [newIdolSize, setNewIdolSize] = useState<string>('MINOR'); // Default size
  const [prefixes, setPrefixes] = useState<Modifier[]>([{ text: '', value: '' }]);
  const [suffixes, setSuffixes] = useState<Modifier[]>([{ text: '', value: '' }]);
  
  // Available mods based on idol type
  const [availablePrefixes, setAvailablePrefixes] = useState<Mod[]>([]);
  const [availableSuffixes, setAvailableSuffixes] = useState<Mod[]>([]);
  
  // Modified: Use an array of content types for prefixes (one per prefix)
  const [prefixContentTypes, setPrefixContentTypes] = useState<Array<ContentType | ''>>(['']);
  
  // Modified: Use an array of content types for suffixes (one per suffix)
  const [suffixContentTypes, setSuffixContentTypes] = useState<Array<ContentType | ''>>(['']);
  
  // Paste functionality
  const [showPasteInput, setShowPasteInput] = useState<boolean>(false);
  const [pasteInput, setPasteInput] = useState<string>('');
  const [pasteError, setPasteError] = useState<string>('');

  // Update available mods when idol type changes
  useEffect(() => {
    const idolSize = getIdolSizeFromItemType(newIdolSize);
    const newPrefixes = getAvailablePrefixes(idolSize);
    const newSuffixes = getAvailableSuffixes(idolSize);
    
    setAvailablePrefixes(newPrefixes);
    setAvailableSuffixes(newSuffixes);
  }, [newIdolSize]);

  // Update name field when switching sizes
  useEffect(() => {
    if (newIdolSize !== 'UNIQUE') {
      setNewIdolName(''); // Reset name when not UNIQUE
    }
  }, [newIdolSize]);

  // Update prefixContentTypes when prefixes change length
  useEffect(() => {
    // Ensure we have the right number of content type entries
    if (prefixes.length !== prefixContentTypes.length) {
      // If more prefixes, add empty content types
      if (prefixes.length > prefixContentTypes.length) {
        setPrefixContentTypes([...prefixContentTypes, ...Array(prefixes.length - prefixContentTypes.length).fill('')]);
      } else {
        // If fewer prefixes, trim the content types array
        setPrefixContentTypes(prefixContentTypes.slice(0, prefixes.length));
      }
    }
  }, [prefixes.length, prefixContentTypes]);

  // Update suffixContentTypes when suffixes change length
  useEffect(() => {
    // Ensure we have the right number of content type entries
    if (suffixes.length !== suffixContentTypes.length) {
      // If more suffixes, add empty content types
      if (suffixes.length > suffixContentTypes.length) {
        setSuffixContentTypes([...suffixContentTypes, ...Array(suffixes.length - suffixContentTypes.length).fill('')]);
      } else {
        // If fewer suffixes, trim the content types array
        setSuffixContentTypes(suffixContentTypes.slice(0, suffixes.length));
      }
    }
  }, [suffixes.length, suffixContentTypes]);

  // Modified: Create handlers for content type changes
  const setSelectedPrefixContentType = useCallback((index: number, type: ContentType | '') => {
    const newContentTypes = [...prefixContentTypes];
    newContentTypes[index] = type;
    setPrefixContentTypes(newContentTypes);
  }, [prefixContentTypes]);

  const setSelectedSuffixContentType = useCallback((index: number, type: ContentType | '') => {
    const newContentTypes = [...suffixContentTypes];
    newContentTypes[index] = type;
    setSuffixContentTypes(newContentTypes);
  }, [suffixContentTypes]);

  // Handle paste event - fixed: define this before it's referenced
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const text = e.clipboardData?.getData('text') || '';
    setPasteInput(text);
    
    try {
      const parsedIdol = parseIdolFromPaste(text);
      if (parsedIdol) {
        onAddIdol(parsedIdol);
        setPasteInput('');
        setShowPasteInput(false);
        onClose();
      } else {
        setPasteError('Could not parse idol from clipboard. Make sure you are copying a valid idol.');
      }
    } catch (err) {
      setPasteError(`Error parsing idol: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [onAddIdol, onClose]);

  // Handle direct paste submission
  const handleDirectPaste = useCallback(() => {
    try {
      const parsedIdol = parseIdolFromPaste(pasteInput);
      if (parsedIdol) {
        onAddIdol(parsedIdol);
        setPasteInput('');
        setShowPasteInput(false);
        onClose();
      } else {
        setPasteError('Could not parse idol from text. Make sure you are pasting a valid idol.');
      }
    } catch (err) {
      setPasteError(`Error parsing idol: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [pasteInput, onAddIdol, onClose]);

  // Validate and create regular idol
  const createRegularIdol = useCallback(() => {
    // Validate at least one prefix or suffix is present with valid configuration
    const validPrefixes = prefixes.filter(p => {
      if (!p.text) return false;
      const prefixMod = availablePrefixes.find(mod => mod.description === p.text);
      // If has range, need value; otherwise just text is needed
      return prefixMod && (!prefixMod.range || p.value);
    });
    
    const validSuffixes = suffixes.filter(s => {
      if (!s.text) return false;
      const suffixMod = availableSuffixes.find(mod => mod.description === s.text);
      // If has range, need value; otherwise just text is needed
      return suffixMod && (!suffixMod.range || s.value);
    });
    
    if (validPrefixes.length === 0 && validSuffixes.length === 0) {
      throw new Error("Please add at least one prefix or suffix to create an idol.");
    }
    
    // Convert prefixes and suffixes to stats array
    const stats: Stat[] = [];
    
    // Process prefixes
    for (const prefix of validPrefixes) {
      const prefixMod = availablePrefixes.find(p => p.description === prefix.text);
      if (prefixMod) {
        stats.push({
          explicitType: "prefix",
          value: prefixMod.range ? (parseFloat(prefix.value) || 0) : 1, // Default to 1 if no range
          contentType: prefixMod.contentType,
          modId: prefixMod.id,
        });
      }
    }
    
    // Process suffixes
    for (const suffix of validSuffixes) {
      const suffixMod = availableSuffixes.find(s => s.description === suffix.text);
      if (suffixMod) {
        stats.push({
          explicitType: "suffix",
          value: suffixMod.range ? (parseFloat(suffix.value) || 0) : 1, // Default to 1 if no range
          contentType: suffixMod.contentType,
          modId: suffixMod.id,
        });
      }
    }
    
    const itemType = ITEM_TYPES[newIdolSize];
    if (!itemType) {
      throw new Error("Invalid idol type selected.");
    }
    
    return {
      id: `custom-${Date.now()}`,
      size: itemType,
      name: newIdolName || itemType.name,
      stats,
    };
  }, [newIdolName, newIdolSize, prefixes, suffixes, availablePrefixes, availableSuffixes]);

  // Reset form values
  const resetForm = useCallback(() => {
    setNewIdolName('');
    setNewIdolSize('MINOR');
    setPrefixes([{ text: '', value: '' }]);
    setSuffixes([{ text: '', value: '' }]);
    setPrefixContentTypes(['']);
    setSuffixContentTypes(['']);
    setShowPasteInput(false);
    setPasteInput('');
    setPasteError('');
  }, []);

  // Check if regular form is valid
  const isRegularFormValid = useCallback(() => {
    return prefixes.some(p => p.text && p.value) || suffixes.some(s => s.text && s.value);
  }, [prefixes, suffixes]);

  return {
    // State
    newIdolName,
    setNewIdolName,
    newIdolSize,
    setNewIdolSize,
    prefixes,
    setPrefixes,
    suffixes,
    setSuffixes,
    availablePrefixes,
    availableSuffixes,
    prefixContentTypes,
    setSelectedPrefixContentType,
    suffixContentTypes,
    setSelectedSuffixContentType,
    showPasteInput,
    setShowPasteInput,
    pasteInput,
    setPasteInput,
    pasteError,
    setPasteError,
    
    // Methods
    handlePaste,
    handleDirectPaste,
    createRegularIdol,
    resetForm,
    isRegularFormValid,
  };
};

export default useIdolForm;